import React, { useEffect, useMemo, useRef, useState } from 'react';
import { showToast } from '../utils';
import { LIVE_AMAZON_ADS_IMPORTED_AT, LIVE_AMAZON_ADS_ROWS } from '../data/liveAmazonAdsSnapshot';

export interface AdsAnalyticsItem {
  id: string;
  title: string;
  image: string;
  sku: string;
  asin: string;
  price: number;
  netBeforeAds: number;
  breakEvenAcos: number;
}

interface AdRow {
  date: string;
  adId?: string;
  campaign: string;
  adGroup?: string;
  sku: string;
  asin: string;
  name?: string;
  price?: number;
  state?: string;
  status?: string;
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
  orders: number;
  units: number;
}

interface StoredReport {
  source: 'live' | 'import';
  fileName?: string;
  loadedAt: string;
  rows: AdRow[];
}

const STORAGE_KEY = 'aurelius-ads-report-v2-1002';

const money = (n: number) =>
  `$${(Number.isFinite(n) ? n : 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (n: number) => Math.round(n || 0).toLocaleString('en-US');
const pct = (n: number) => (Number.isFinite(n) ? `${n.toFixed(1)}%` : '—');

/** Minimal RFC-4180 CSV parser (handles quoted fields, commas and newlines inside quotes). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const src = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',' || c === '\t') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      if (row.some((f) => f.trim() !== '')) rows.push(row);
      row = [];
      field = '';
    } else field += c;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== '')) rows.push(row);
  return rows;
}

const norm = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, '');
const num = (v: string | undefined) => {
  if (v == null) return 0;
  const n = parseFloat(String(v).replace(/[$,%\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** Maps an Amazon Ads console report (Sponsored Products › Advertised product / Campaign) to AdRow[]. */
export function rowsFromAmazonCsv(text: string): AdRow[] {
  const table = parseCsv(text);
  if (table.length < 2) return [];
  const headers = table[0].map(norm);
  const find = (...cands: string[]) => {
    for (const c of cands) {
      const idx = headers.findIndex((h) => h === c);
      if (idx >= 0) return idx;
    }
    for (const c of cands) {
      const idx = headers.findIndex((h) => h.includes(c));
      if (idx >= 0) return idx;
    }
    return -1;
  };
  const col = {
    date: find('date', 'startdate', 'day'),
    campaign: find('campaignname', 'campaign'),
    sku: find('advertisedsku', 'sku'),
    asin: find('advertisedasin', 'asin'),
    impressions: find('impressions'),
    clicks: find('clicks'),
    spend: find('spend', 'cost', 'totalcost'),
    sales: find('7daytotalsales', '14daytotalsales', 'sales7d', 'totalsales', 'sales'),
    orders: find('7daytotalorders', '14daytotalorders', 'purchases7d', 'totalorders', 'orders', 'purchases'),
    units: find('7daytotalunits', 'unitssoldclicks7d', 'totalunits', 'units')
  };
  if (col.impressions < 0 && col.clicks < 0 && col.spend < 0) return [];
  return table.slice(1).map((r) => {
    const rawDate = col.date >= 0 ? String(r[col.date] || '').trim() : '';
    const parsed = rawDate ? new Date(rawDate) : null;
    return {
      date: parsed && !isNaN(parsed.getTime()) ? parsed.toISOString().slice(0, 10) : '',
      campaign: col.campaign >= 0 ? String(r[col.campaign] || '').trim() : '',
      sku: col.sku >= 0 ? String(r[col.sku] || '').trim() : '',
      asin: col.asin >= 0 ? String(r[col.asin] || '').trim() : '',
      impressions: num(r[col.impressions]),
      clicks: num(r[col.clicks]),
      spend: num(r[col.spend]),
      sales: num(r[col.sales]),
      orders: num(r[col.orders]),
      units: num(r[col.units])
    };
  });
}

function summarize(rows: AdRow[]) {
  const t = rows.reduce(
    (a, r) => ({
      impressions: a.impressions + r.impressions,
      clicks: a.clicks + r.clicks,
      spend: a.spend + r.spend,
      sales: a.sales + r.sales,
      orders: a.orders + r.orders,
      units: a.units + r.units
    }),
    { impressions: 0, clicks: 0, spend: 0, sales: 0, orders: 0, units: 0 }
  );
  return {
    ...t,
    ctr: t.impressions ? (t.clicks / t.impressions) * 100 : NaN,
    cpc: t.clicks ? t.spend / t.clicks : NaN,
    acos: t.sales ? (t.spend / t.sales) * 100 : NaN,
    roas: t.spend ? t.sales / t.spend : NaN,
    cvr: t.clicks ? (t.orders / t.clicks) * 100 : NaN
  };
}

const card: React.CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #E4E4E7',
  borderRadius: 12,
  padding: '18px 20px'
};
const label: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#71717A',
  marginBottom: 6
};
const btn: React.CSSProperties = {
  border: '1px solid #E4E4E7',
  background: '#FFFFFF',
  color: '#09090B',
  borderRadius: 8,
  padding: '9px 14px',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer'
};
const btnDark: React.CSSProperties = { ...btn, background: '#09090B', color: '#FFFFFF', border: '1px solid #09090B' };

export const AdsAnalytics: React.FC<{ items: AdsAnalyticsItem[]; active: boolean }> = ({ items, active }) => {
  const defaultLiveReport: StoredReport = {
    source: 'live',
    fileName: 'Amazon Campaign Manager (ENTITY4D02Z7N448BV)',
    loadedAt: LIVE_AMAZON_ADS_IMPORTED_AT,
    rows: LIVE_AMAZON_ADS_ROWS
  };
  const [report, setReport] = useState<StoredReport | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as StoredReport;
        if (
          parsed &&
          Array.isArray(parsed.rows) &&
          parsed.rows.length >= LIVE_AMAZON_ADS_ROWS.length
        ) {
          return parsed;
        }
      }
    } catch (_) {}
    return defaultLiveReport;
  });
  const [rangeDays, setRangeDays] = useState<number>(14);
  const [loadingLive, setLoadingLive] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<'spend' | 'sales' | 'acos' | 'clicks' | 'profit'>('spend');
  const fileRef = useRef<HTMLInputElement>(null);
  const ADS_PAGE_SIZE = 25;
  const [visibleCount, setVisibleCount] = useState<number>(ADS_PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const persist = (r: StoredReport | null) => {
    setReport(r);
    try {
      if (r) localStorage.setItem(STORAGE_KEY, JSON.stringify(r));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  };

  const loadLive = async (quiet = false) => {
    setLoadingLive(true);
    setLiveError(null);
    try {
      const res = await fetch(`/api/amazon-ads/analytics?days=${rangeDays}`);
      const data = res.ok ? await res.json() : null;
      if (data?.ok && Array.isArray(data.rows)) {
        persist({ source: 'live', loadedAt: new Date().toISOString(), rows: data.rows });
        if (!quiet) showToast(`Loaded ${data.rows.length} rows from Amazon Ads reporting.`);
      } else {
        setLiveError(data?.error || 'Live Amazon Ads reporting is only available from the local server (localhost:3456).');
      }
    } catch (_) {
      setLiveError('Live Amazon Ads reporting is only available from the local server (localhost:3456).');
    } finally {
      setLoadingLive(false);
    }
  };

  useEffect(() => {
    if (active && !report) loadLive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const handleFile = async (file: File) => {
    const text = await file.text();
    const rows = rowsFromAmazonCsv(text);
    if (!rows.length) {
      showToast('Could not read that file. Export a Sponsored Products report as CSV from Amazon Ads › Reports.');
      return;
    }
    persist({ source: 'import', fileName: file.name, loadedAt: new Date().toISOString(), rows });
    showToast(`Imported ${rows.length} rows from ${file.name}.`);
  };

  const rows = useMemo(() => {
    const all = report?.rows || [];
    const dated = all.filter((r) => r.date);
    if (!dated.length) return all;
    const latest = dated.reduce((m, r) => (r.date > m ? r.date : m), dated[0].date);
    const cutoff = new Date(new Date(latest).getTime() - (rangeDays - 1) * 86400000).toISOString().slice(0, 10);
    return all.filter((r) => !r.date || r.date >= cutoff);
  }, [report, rangeDays]);

  const totals = useMemo(() => summarize(rows), [rows]);

  const daily = useMemo(() => {
    const map = new Map<string, { spend: number; sales: number }>();
    for (const r of rows) {
      if (!r.date) continue;
      const d = map.get(r.date) || { spend: 0, sales: 0 };
      d.spend += r.spend;
      d.sales += r.sales;
      map.set(r.date, d);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, v]) => ({ date, ...v }));
  }, [rows]);

  const byProduct = useMemo(() => {
    const map = new Map<string, AdRow[]>();
    for (const r of rows) {
      const key = r.sku || r.asin || r.campaign || 'Unattributed';
      map.set(key, [...(map.get(key) || []), r]);
    }
    const out = [...map.entries()].map(([key, rs]) => {
      const s = summarize(rs);
      const item =
        items.find((i) => i.sku && rs[0].sku && i.sku.toLowerCase() === rs[0].sku.toLowerCase()) ||
        items.find((i) => i.asin && rs[0].asin && i.asin === rs[0].asin) ||
        null;
      const grossProfit = item ? s.orders * item.netBeforeAds : NaN;
      const profitAfterAds = Number.isFinite(grossProfit) ? grossProfit - s.spend : NaN;
      let status: 'profit' | 'loss' | 'nosales' | 'delivering' | 'unknown' = 'unknown';
      if (s.spend > 0 && s.orders === 0) status = 'nosales';
      else if (item && Number.isFinite(s.acos)) status = s.acos <= item.breakEvenAcos ? 'profit' : 'loss';
      else if (rs[0].status === 'AD_STATUS_LIVE' || rs[0].state === 'ENABLED') status = 'delivering';
      return {
        key,
        item,
        adId: rs[0].adId,
        asin: rs[0].asin,
        adName: rs[0].name,
        adPrice: rs[0].price,
        ...s,
        profitAfterAds,
        status,
        breakEvenAcos: item?.breakEvenAcos ?? NaN
      };
    });
    const val = (x: (typeof out)[number]) =>
      sortKey === 'profit' ? (Number.isFinite(x.profitAfterAds) ? x.profitAfterAds : -1e9) : sortKey === 'acos' ? (Number.isFinite(x.acos) ? x.acos : 1e9) : x[sortKey];
    return out.sort((a, b) => (sortKey === 'acos' ? val(a) - val(b) : val(b) - val(a)));
  }, [rows, items, sortKey]);

  const wastedSpend = byProduct.filter((p) => p.status === 'nosales').reduce((s, p) => s + p.spend, 0);
  const totalProfitAfterAds = byProduct.reduce((s, p) => s + (Number.isFinite(p.profitAfterAds) ? p.profitAfterAds : 0), 0);
  const avgBreakEven = items.length ? items.reduce((s, i) => s + i.breakEvenAcos, 0) / items.length : NaN;
  const maxDaily = Math.max(1, ...daily.map((d) => Math.max(d.spend, d.sales)));

  const visibleProducts = byProduct.slice(0, visibleCount);
  const hasMore = visibleCount < byProduct.length;

  useEffect(() => {
    setVisibleCount(ADS_PAGE_SIZE);
  }, [sortKey, rangeDays, report]);

  useEffect(() => {
    if (!active || !hasMore) return;
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) => Math.min(byProduct.length, prev + ADS_PAGE_SIZE));
        }
      },
      { rootMargin: '280px 0px', threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [active, hasMore, byProduct.length]);

  const hasData = rows.length > 0;

  return (
    <div>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 20
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#09090B', letterSpacing: '-0.02em' }}>Ads Analytics</h2>
          <div style={{ fontSize: 13, color: '#52525B', marginTop: 4 }}>
            {report
              ? `${report.source === 'live' ? 'Live from Amazon Ads Reporting API' : `Imported report · ${report.fileName}`} · loaded ${new Date(
                  report.loadedAt
                ).toLocaleString()}`
              : 'No ad performance data loaded yet.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div role="group" aria-label="Date range" style={{ display: 'flex', border: '1px solid #E4E4E7', borderRadius: 8, overflow: 'hidden' }}>
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={rangeDays === d}
                onClick={() => setRangeDays(d)}
                style={{
                  padding: '9px 12px',
                  fontSize: 13,
                  fontWeight: 700,
                  border: 'none',
                  cursor: 'pointer',
                  background: rangeDays === d ? '#09090B' : '#FFFFFF',
                  color: rangeDays === d ? '#FFFFFF' : '#3F3F46'
                }}
              >
                {d}d
              </button>
            ))}
          </div>
          <button type="button" style={btnDark} onClick={() => loadLive()} disabled={loadingLive}>
            {loadingLive ? 'Fetching from Amazon…' : '↻ Refresh from Amazon'}
          </button>
          <button type="button" style={btn} onClick={() => fileRef.current?.click()}>
            ⇪ Import report (CSV)
          </button>
          {report && (
            <button type="button" style={{ ...btn, color: '#B91C1C' }} onClick={() => persist(null)}>
              Clear
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.tsv,.txt,text/csv"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {liveError && (
        <div
          role="status"
          style={{
            ...card,
            background: '#FFFBEB',
            borderColor: '#FDE68A',
            color: '#78350F',
            fontSize: 13,
            marginBottom: 20,
            lineHeight: 1.55
          }}
        >
          <strong>Live ad data isn't connected:</strong> {liveError}
          <div style={{ marginTop: 6 }}>
            Until the Ads API is authorized, import a report: Amazon Ads console → <strong>Measurement &amp; Reporting → Sponsored ads reports</strong> →
            Create report → <strong>Sponsored Products · Advertised product</strong> · Time unit <strong>Daily</strong> · download as CSV, then click
            “Import report”.
          </div>
        </div>
      )}

      {!hasData ? (
        <div style={{ ...card, textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#09090B', marginBottom: 8 }}>No ad performance data yet</div>
          <div style={{ fontSize: 14, color: '#52525B', maxWidth: 560, margin: '0 auto 18px', lineHeight: 1.6 }}>
            Spend, clicks, sales and ACOS will show here once Amazon reports activity for your campaigns. New campaigns usually start
            reporting within 24–48 hours.
          </div>
          <button type="button" style={btnDark} onClick={() => fileRef.current?.click()}>
            ⇪ Import Amazon Ads report
          </button>
        </div>
      ) : (
        <>
          {/* KPI grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { k: 'Ad Spend', v: money(totals.spend) },
              { k: 'Ad Sales', v: money(totals.sales) },
              {
                k: 'ACOS',
                v: pct(totals.acos),
                sub: Number.isFinite(avgBreakEven) ? `Break-even ≈ ${avgBreakEven.toFixed(0)}%` : undefined,
                tone: Number.isFinite(totals.acos) && Number.isFinite(avgBreakEven) ? (totals.acos <= avgBreakEven ? 'good' : 'bad') : undefined
              },
              { k: 'ROAS', v: Number.isFinite(totals.roas) ? `${totals.roas.toFixed(2)}×` : '—' },
              { k: 'Orders', v: int(totals.orders), sub: `${int(totals.units)} units` },
              { k: 'Impressions', v: int(totals.impressions) },
              { k: 'Clicks', v: int(totals.clicks), sub: `CTR ${pct(totals.ctr)}` },
              { k: 'Avg CPC', v: Number.isFinite(totals.cpc) ? money(totals.cpc) : '—' },
              { k: 'Conversion', v: pct(totals.cvr), sub: 'orders ÷ clicks' },
              {
                k: 'Profit After Ads',
                v: money(totalProfitAfterAds),
                sub: 'est. from product margins',
                tone: totalProfitAfterAds >= 0 ? 'good' : 'bad'
              }
            ].map((m) => (
              <div key={m.k} style={card}>
                <div style={label}>{m.k}</div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                    color: m.tone === 'good' ? '#15803D' : m.tone === 'bad' ? '#B91C1C' : '#09090B'
                  }}
                >
                  {m.v}
                </div>
                {m.sub && <div style={{ fontSize: 12, color: '#71717A', marginTop: 2 }}>{m.sub}</div>}
              </div>
            ))}
          </div>

          {/* Insights */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div style={{ ...card, borderLeft: '4px solid #B91C1C' }}>
              <div style={label}>Wasted spend</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#B91C1C', fontVariantNumeric: 'tabular-nums' }}>{money(wastedSpend)}</div>
              <div style={{ fontSize: 13, color: '#52525B', marginTop: 4 }}>
                on {byProduct.filter((p) => p.status === 'nosales').length} products with clicks but no orders — consider lowering bids or pausing.
              </div>
            </div>
            <div style={{ ...card, borderLeft: '4px solid #15803D' }}>
              <div style={label}>Profitable products</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#15803D', fontVariantNumeric: 'tabular-nums' }}>
                {byProduct.filter((p) => p.status === 'profit').length}
              </div>
              <div style={{ fontSize: 13, color: '#52525B', marginTop: 4 }}>ACOS below their break-even — candidates for higher bids.</div>
            </div>
            <div style={{ ...card, borderLeft: '4px solid #9E782F' }}>
              <div style={label}>Over break-even</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#9E782F', fontVariantNumeric: 'tabular-nums' }}>
                {byProduct.filter((p) => p.status === 'loss').length}
              </div>
              <div style={{ fontSize: 13, color: '#52525B', marginTop: 4 }}>Selling, but ads cost more than the margin per order.</div>
            </div>
          </div>

          {/* Daily trend */}
          {daily.length > 1 && (
            <div style={{ ...card, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#09090B' }}>Daily spend vs. sales</div>
                <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#52525B' }}>
                  <span>
                    <span style={{ display: 'inline-block', width: 10, height: 10, background: '#09090B', borderRadius: 2, marginRight: 5 }} />
                    Spend
                  </span>
                  <span>
                    <span style={{ display: 'inline-block', width: 10, height: 10, background: '#C9A24A', borderRadius: 2, marginRight: 5 }} />
                    Sales
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 180, overflowX: 'auto' }}>
                {daily.map((d) => (
                  <div
                    key={d.date}
                    title={`${d.date}\nSpend ${money(d.spend)}\nSales ${money(d.sales)}`}
                    style={{ flex: '1 0 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 150 }}>
                      <div style={{ width: 10, height: `${(d.spend / maxDaily) * 100}%`, background: '#09090B', borderRadius: '2px 2px 0 0' }} />
                      <div style={{ width: 10, height: `${(d.sales / maxDaily) * 100}%`, background: '#C9A24A', borderRadius: '2px 2px 0 0' }} />
                    </div>
                    <div style={{ fontSize: 10, color: '#71717A', fontVariantNumeric: 'tabular-nums' }}>{d.date.slice(5)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-product table */}
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid #E4E4E7',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 800, color: '#09090B' }}>Performance by product ({byProduct.length})</div>
              <label style={{ fontSize: 12, color: '#52525B', display: 'flex', alignItems: 'center', gap: 6 }}>
                Sort by
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as typeof sortKey)}
                  style={{ border: '1px solid #E4E4E7', borderRadius: 6, padding: '5px 8px', fontSize: 12, fontWeight: 600 }}
                >
                  <option value="spend">Spend</option>
                  <option value="sales">Sales</option>
                  <option value="clicks">Clicks</option>
                  <option value="acos">ACOS (lowest)</option>
                  <option value="profit">Profit after ads</option>
                </select>
              </label>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F8F8F9', textAlign: 'left' }}>
                    {['Product', 'Impr.', 'Clicks', 'CTR', 'Spend', 'Sales', 'Orders', 'ACOS', 'Break-even', 'Profit after ads', 'Status'].map((h) => (
                      <th key={h} style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', color: '#71717A', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleProducts.map((p) => (
                    <tr key={p.key} style={{ borderTop: '1px solid #E4E4E7' }}>
                      <td style={{ padding: '10px 14px', maxWidth: 300 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {p.item?.image && (
                            <img
                              src={p.item.image}
                              alt=""
                              style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 6, border: '1px solid #E4E4E7', flexShrink: 0 }}
                            />
                          )}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: '#09090B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {p.item?.title || p.adName || p.key}
                            </div>
                            <div style={{ fontSize: 11, color: '#71717A', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <code>{p.key}</code>
                              {p.asin && <span>· ASIN {p.asin}</span>}
                              {p.adId && <span>· Ad {p.adId}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums' }}>{int(p.impressions)}</td>
                      <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums' }}>{int(p.clicks)}</td>
                      <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums' }}>{pct(p.ctr)}</td>
                      <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{money(p.spend)}</td>
                      <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums' }}>{money(p.sales)}</td>
                      <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums' }}>{int(p.orders)}</td>
                      <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{pct(p.acos)}</td>
                      <td style={{ padding: '10px 14px', fontVariantNumeric: 'tabular-nums', color: '#52525B' }}>{pct(p.breakEvenAcos)}</td>
                      <td
                        style={{
                          padding: '10px 14px',
                          fontVariantNumeric: 'tabular-nums',
                          fontWeight: 700,
                          color: !Number.isFinite(p.profitAfterAds) ? '#71717A' : p.profitAfterAds >= 0 ? '#15803D' : '#B91C1C'
                        }}
                      >
                        {Number.isFinite(p.profitAfterAds) ? `${p.profitAfterAds >= 0 ? '+' : '−'}${money(Math.abs(p.profitAfterAds))}` : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const map = {
                            profit: { t: '● Profitable', bg: '#ECFDF5', c: '#065F46' },
                            delivering: { t: '● Delivering (Live)', bg: '#ECFDF5', c: '#065F46' },
                            loss: { t: '▲ Over break-even', bg: '#FEF3C7', c: '#92400E' },
                            nosales: { t: '✕ No orders', bg: '#FEE2E2', c: '#991B1B' },
                            unknown: { t: '○ Not enough data', bg: '#F4F4F5', c: '#52525B' }
                          }[p.status];
                          return (
                            <span style={{ background: map.bg, color: map.c, padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                              {map.t}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              ref={sentinelRef}
              style={{
                padding: '14px 18px',
                borderTop: '1px solid #E4E4E7',
                background: '#F8F8F9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12
              }}
            >
              <span style={{ fontSize: 12, color: '#3F3F46', fontVariantNumeric: 'tabular-nums' }}>
                Showing <strong>{visibleProducts.length}</strong> of <strong>{byProduct.length}</strong> advertised products
                {hasMore ? ' · Scroll down to auto-fetch more' : ' · All products loaded'}
              </span>
              {hasMore ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    style={btnDark}
                    onClick={() => setVisibleCount((prev) => Math.min(byProduct.length, prev + ADS_PAGE_SIZE))}
                  >
                    Load {Math.min(ADS_PAGE_SIZE, byProduct.length - visibleProducts.length)} More ↓
                  </button>
                  <button type="button" style={btn} onClick={() => setVisibleCount(byProduct.length)}>
                    Show All ({byProduct.length})
                  </button>
                </div>
              ) : (
                byProduct.length > ADS_PAGE_SIZE && (
                  <button type="button" style={btn} onClick={() => setVisibleCount(ADS_PAGE_SIZE)}>
                    Collapse to Top {ADS_PAGE_SIZE} ↑
                  </button>
                )
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
